import {defineStore} from 'pinia';
import {ref} from 'vue';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

export const useWeightTrackingStore =
  /**
   * In-memory cache of weights the user has entered during the CURRENT run of a
   * session, keyed by question `contentId` (value stored in grams).
   *
   * Scope rationale (weight pre-fill ticket):
   * - Within a session: a weight field is pre-filled with the value the user
   *   already entered earlier in the same session.
   * - Across sessions: nothing carries over — each new session starts fresh.
   * - Across enrollments: nor does anything, because the id scoping this cache is
   *   the user's row for that session rather than the session content (IPD-3030).
   *
   * This store only holds the LIVE value (what the user just typed). Surviving a
   * reload mid-session is handled by deriving from the server answer in
   * `use-session-weight.ts`; the store deliberately does NOT use localStorage so
   * values never leak across sessions/interventions (unlike the previous
   * localStorage-backed, intervention-wide implementation).
   */
  defineStore('weight-tracking-store', () => {
    const runId = ref<UuidDTO | null>(null);
    const byContentId = ref<Record<UuidDTO, number>>({});
    const lastContentId = ref<UuidDTO | null>(null);

    /**
     * Every entry point takes `userSessionId` — the user's row for a session, which is a different
     * id per enrollment — and NOT the route's session id, which names the content and repeats
     * every time the intervention is taken. Both are bare `UuidDTO`, so the wrong one still
     * compiles; the name is what carries the distinction (IPD-3030).
     */
    function ensureRun(userSessionId: UuidDTO): void {
      if (runId.value !== userSessionId) {
        runId.value = userSessionId;
        byContentId.value = {};
        lastContentId.value = null;
      }
    }

    /** Records a weight (grams) for a question within this run of the session. */
    function setWeight(userSessionId: UuidDTO, contentId: UuidDTO, grams: number): void {
      ensureRun(userSessionId);
      byContentId.value = {...byContentId.value, [contentId]: grams};
      lastContentId.value = contentId;
    }

    /** Last value entered for a specific question, only within this run of the session. */
    function getWeight(userSessionId: UuidDTO, contentId: UuidDTO): number | null {
      if (runId.value !== userSessionId) {
        return null;
      }
      return byContentId.value[contentId] ?? null;
    }

    /** Most recent weight entered in this run (used by the % goal display). */
    function getLatestWeight(userSessionId: UuidDTO): number | null {
      if (runId.value !== userSessionId || lastContentId.value === null) {
        return null;
      }
      return byContentId.value[lastContentId.value] ?? null;
    }

    /** Clears everything (used on logout, matching the other stores' convention). */
    function clearStore(): void {
      runId.value = null;
      byContentId.value = {};
      lastContentId.value = null;
    }

    // `runId`/`byContentId`/`lastContentId` are intentionally NOT exposed:
    // all access goes through the run-guarded getters/actions below.
    return {
      setWeight,
      getWeight,
      getLatestWeight,
      clearStore,
    };
  });
